import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Typography,
  Snackbar,
  InputAdornment,
} from '@mui/material';
//import { Add, Edit, Delete, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Add, Edit, Delete, Person, PersonAdd } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import '../../styles/TeamInviteDialog.css';

const sidebarAwareDialogSx = (maxPx: number) => ({
  '& .MuiDialog-container': {
    paddingLeft: '16px',
    paddingRight: '16px',
    boxSizing: 'border-box',
  },
  '& .MuiDialog-paper': {
    width: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    maxWidth: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    margin: 'auto',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
});

/** Invite dialog: top-anchored + max height so the header stays visible at sub-100% browser zoom. */
const teamInviteDialogSx = {
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    paddingTop: 'max(16px, min(88px, 12vh))',
    paddingBottom: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
    boxSizing: 'border-box',
    overflowY: 'auto',
  },
  '& .MuiDialog-paper': {
    width: 'min(420px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))',
    maxWidth: 'min(420px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))',
    maxHeight: 'min(calc(100vh - 32px), calc(100dvh - 32px))',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
};

interface TeamMember {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  joinedAt: string;
  addedByEmail?: string | null;
}

const TeamMembers: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [currentUserCanRemoveMembers, setCurrentUserCanRemoveMembers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [upgradePlanDialogOpen, setUpgradePlanDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [roleChangeConfirmDialogOpen, setRoleChangeConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [newMemberFirstName, setNewMemberFirstName] = useState('');
  const [newMemberLastName, setNewMemberLastName] = useState('');
  const [newMemberFirstNameError, setNewMemberFirstNameError] = useState<string | null>(null);
  const [newMemberLastNameError, setNewMemberLastNameError] = useState<string | null>(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberEmailError, setNewMemberEmailError] = useState<string | null>(null);
  const [newMemberRole, setNewMemberRole] = useState('MEMBER');
  const [addingMember, setAddingMember] = useState(false);
  const [editRole, setEditRole] = useState('MEMBER');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [allowedEmailDomain, setAllowedEmailDomain] = useState<string | null>(null);
  const [organizationNameDomain, setOrganizationNameDomain] = useState<string | null>(null);
  const [organizationSlug, setOrganizationSlug] = useState<string | null>(null);

  const notifyTeamMembersChanged = () => {
    // Let other Manage Account tabs (e.g. Licenses) refresh immediately without requiring a full page reload.
    window.dispatchEvent(new Event('manageAccount:teamMembersChanged'));
  };

  const normalizeEmailDomain = (raw: string | null | undefined): string | null => {
    const trimmed = raw?.trim();
    if (!trimmed) return null;
    return trimmed.includes('@')
      ? trimmed.substring(trimmed.lastIndexOf('@') + 1).trim().toLowerCase()
      : trimmed.toLowerCase();
  };

  const getAddMemberEmailDomain = (): string | null => {
    const normalizedAllowedDomain = normalizeEmailDomain(allowedEmailDomain);
    const fallbackOrganizationDomain = organizationNameDomain?.trim().toLowerCase() || null;
    return normalizedAllowedDomain ?? fallbackOrganizationDomain;
  };

  const buildAddMemberEmail = (localPart: string): string => {
    const trimmedLocal = localPart.trim();
    const domain = getAddMemberEmailDomain();
    if (!domain) return trimmedLocal;
    const username = trimmedLocal.includes('@') ? trimmedLocal.split('@')[0] : trimmedLocal;
    return `${username}@${domain}`;
  };

  const resetAddMemberForm = () => {
    setNewMemberFirstName('');
    setNewMemberLastName('');
    setNewMemberFirstNameError(null);
    setNewMemberLastNameError(null);
    setNewMemberEmail('');
    setNewMemberEmailError(null);
    setNewMemberRole('MEMBER');
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    resetAddMemberForm();
  };

  const isFreePlan = () => (user?.planType || 'FREE').trim().toUpperCase() === 'FREE';

  const roleConsumesLicenseSeat = (role: string) => role.trim().toUpperCase() !== 'ADMIN';

  const hasAvailableLicenseForRole = async (role: string): Promise<boolean> => {
    if (!roleConsumesLicenseSeat(role)) return true;
    try {
      const license = await apiService.getLicenseUsage();
      return (license.availableLicenses ?? 0) > 0;
    } catch {
      return false;
    }
  };

  const showNoLicenseAvailable = () => {
    if (isFreePlan()) {
      setUpgradePlanDialogOpen(true);
      return;
    }
    setError(
      'License limit reached. All licenses are already in use. Please purchase additional licenses to add more members.'
    );
  };

  const handleOpenAddMember = () => {
    resetAddMemberForm();
    setAddDialogOpen(true);
  };

  useEffect(() => {
    loadMembers();
  }, []);

  useEffect(() => {
    apiService.getPrimaryOrganization().then((org) => {
      setAllowedEmailDomain(org?.allowedEmailDomain ?? null);
      setOrganizationSlug(typeof org?.slug === 'string' ? org.slug.trim().toLowerCase() : null);
      const orgName = typeof org?.name === 'string' ? org.name.trim() : '';
      if (orgName.includes('@')) {
        const extracted = orgName.substring(orgName.lastIndexOf('@') + 1).trim().toLowerCase();
        setOrganizationNameDomain(extracted || null);
      } else if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(orgName)) {
        setOrganizationNameDomain(orgName.toLowerCase());
      } else {
        setOrganizationNameDomain(null);
      }
    }).catch(() => {});
  }, []);

  const loadMembers = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const response = await apiService.getTeamMembers();
      if (response.members) {
        setMembers(response.members);
        setCurrentUserCanRemoveMembers(response.currentUserCanRemoveMembers === true);
        // Effective allowed domain: configured or creator's (only this domain can be added)
        if (response.allowedEmailDomain != null) {
          setAllowedEmailDomain(response.allowedEmailDomain);
        }
      } else if (response.error) {
        setError(response.error);
      }
    } catch (err: any) {
      if (!silent) {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load team members');
      } else {
        console.warn('Team members refresh (silent) failed:', err?.response?.data ?? err?.message);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleAddMember = async () => {
    if (addingMember) return;

    if (!(await hasAvailableLicenseForRole(newMemberRole))) {
      showNoLicenseAvailable();
      return;
    }

    const trimmedFirstName = newMemberFirstName.trim();
    const trimmedLastName = newMemberLastName.trim();
    const trimmedEmail = buildAddMemberEmail(newMemberEmail);
    const addMemberEmailDomain = getAddMemberEmailDomain();

    let hasValidationError = false;
    if (!trimmedFirstName) {
      setNewMemberFirstNameError('First name is required');
      hasValidationError = true;
    } else {
      setNewMemberFirstNameError(null);
    }
    if (!trimmedLastName) {
      setNewMemberLastNameError('Last name is required');
      hasValidationError = true;
    } else {
      setNewMemberLastNameError(null);
    }
    if (hasValidationError) {
      return;
    }

    // Basic email validations shown directly on the email field
    if (!trimmedEmail) {
      setNewMemberEmailError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (addMemberEmailDomain) {
      const username = newMemberEmail.trim().split('@')[0];
      if (!username || !/^[^\s@]+$/.test(username)) {
        setNewMemberEmailError('Please enter a valid email username');
        return;
      }
    } else if (!emailRegex.test(trimmedEmail)) {
      setNewMemberEmailError('Please enter a valid email address');
      return;
    }

    // Email domain restriction (whitelist): only organization domain allowed (case-insensitive)
    const rawAllowedDomain = allowedEmailDomain?.trim();
    const normalizedAllowedDomain = rawAllowedDomain?.includes('@')
      ? rawAllowedDomain.substring(rawAllowedDomain.lastIndexOf('@') + 1).trim().toLowerCase()
      : rawAllowedDomain?.toLowerCase();
    const fallbackOrganizationDomain = organizationNameDomain?.trim().toLowerCase() || null;
    const acceptedDomains = [normalizedAllowedDomain, fallbackOrganizationDomain].filter(
      (d): d is string => !!d
    );

    if (acceptedDomains.length > 0) {
      const at = trimmedEmail.indexOf('@');
      const emailDomain = at >= 0 ? trimmedEmail.substring(at + 1).trim().toLowerCase() : '';
      const emailDomainDotsRemoved = emailDomain.replace(/\./g, '');
      const orgSlugNormalized = organizationSlug?.replace(/\./g, '') || '';
      const isAllowedByDomain = acceptedDomains.includes(emailDomain);
      const isAllowedByOrganizationSlug =
        !!orgSlugNormalized && emailDomainDotsRemoved === orgSlugNormalized;
      const isAllowed = isAllowedByDomain || isAllowedByOrganizationSlug;
      if (!isAllowed) {
        setNewMemberEmailError(`Only organization email addresses (@${acceptedDomains[0]}) are allowed`);
        return;
      }
    }

    try {
      setAddingMember(true);
      setError(null);
      setSuccess(null);
      setNewMemberEmailError(null);
      console.log('Adding team member - Email:', newMemberEmail, 'Role:', newMemberRole);
      const response = await apiService.addTeamMember(trimmedEmail, newMemberRole, {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
      });
      console.log('Add team member response:', response);
      
      if (response.error) {
        let msg = response.error as string | undefined;
        console.error('Error from API:', msg);

        // Show on email field: user not found or organization domain restriction
        if (msg && (msg.toLowerCase().includes('user not found') || msg.toLowerCase().includes('organization email addresses'))) {
          setNewMemberEmailError(msg);
        } else {
          setError(msg || 'Failed to add team member');
        }
      } else {
        setSuccess('Team member added successfully');
        closeAddDialog();
        loadMembers({ silent: true });
        notifyTeamMembersChanged();
      }
    } catch (err: any) {
      console.error('Exception adding team member:', err);
      const errorData = err?.response?.data;
      let apiError =
        (typeof errorData === 'string' ? errorData : undefined) ||
        (errorData?.error as string | undefined) ||
        (errorData?.message as string | undefined);
      console.error('API Error:', apiError);
      console.error('Full error:', err);
      const isNetworkError = err.message === 'Network Error' || err.code === 'ERR_NETWORK' || !err.response;
      
      // Show on email field: user not found or organization domain restriction
      if (apiError && (apiError.toLowerCase().includes('user not found') || apiError.toLowerCase().includes('organization email addresses'))) {
        setNewMemberEmailError(apiError);
      } else if (apiError) {
        setError(apiError);
      } else if (isNetworkError) {
        setError('Unable to add team member. Please check that the server is running and try again.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to add team member. Please check the console for details.');
      }
    } finally {
      setAddingMember(false);
    }
  };

  const getOriginalEditRole = (member: TeamMember) => {
    const isOrgCreator = !member.addedByEmail || member.addedByEmail === '';
    const normalizedOriginalRole =
      member.role === 'ADMIN_MEMBER' ? 'ADMIN_PROJECT_MANAGER' : member.role;
    return isOrgCreator && member.role === 'MEMBER' ? 'ADMIN' : normalizedOriginalRole;
  };

  const performUpdateRole = async () => {
    if (!selectedMember) return;

    const trimmedFirstName = (selectedMember.firstName || '').trim();
    const trimmedLastName = (selectedMember.lastName || '').trim();
    const emailForUpdate = (selectedMember.email || '').trim().toLowerCase();

    try {
      setError(null);
      setSuccess(null);
      const originalRole = getOriginalEditRole(selectedMember);
      const roleChanged = editRole !== originalRole;
      console.log('Updating role for user:', selectedMember.userId, 'to role:', editRole);
      const response = await apiService.updateMemberRole(selectedMember.userId, editRole, {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
      });
      if (response.error) {
        console.error('Error updating role:', response.error);
        setError(response.error);
      } else {
        const updatedUserId = selectedMember.userId;
        const apiRole = response.role ?? editRole;
        setMembers((prev) =>
          prev.map((m) =>
            m.userId === updatedUserId
              ? {
                  ...m,
                  role: apiRole,
                  firstName: response.firstName ?? m.firstName,
                  lastName: response.lastName ?? m.lastName,
                  email: response.email ?? m.email,
                }
              : m
          )
        );
        const successMessage = !roleChanged
          ? 'No changes were made'
          : (typeof response.message === 'string' && response.message.trim()) ||
            'Member role updated successfully';
        const isSelfUpdate = selectedMember.userId === user?.id;
        setSuccess(successMessage);
        setEditDialogOpen(false);
        setSelectedMember(null);
        if (isSelfUpdate && roleChanged) {
          window.setTimeout(() => {
            void logout();
          }, 2500);
        } else {
          await loadMembers({ silent: true });
          notifyTeamMembersChanged();
        }
      }
    } catch (err: any) {
      console.error('Exception updating role:', err);
      const errorData = err?.response?.data;
      let apiError =
        (typeof errorData === 'string' ? errorData : undefined) ||
        (errorData?.message as string | undefined) ||
        (errorData?.error as string | undefined);
      if (apiError === 'Internal Server Error' && errorData?.message) {
        apiError = errorData.message;
      }
      setError(apiError || err.message || 'Failed to update member role');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedMember) return;

    // Profile fields are read-only in the dialog; only role changes are submitted.
    const trimmedFirstName = (selectedMember.firstName || '').trim();
    const trimmedLastName = (selectedMember.lastName || '').trim();
    const emailForUpdate = (selectedMember.email || '').trim().toLowerCase();
    if (!trimmedFirstName) {
      setError('First name is required');
      return;
    }
    if (!trimmedLastName) {
      setError('Last name is required');
      return;
    }
    if (!emailForUpdate) {
      setError('Email is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForUpdate)) {
      setError('Please enter a valid email address');
      return;
    }

    const originalRole = getOriginalEditRole(selectedMember);
    const roleChanged = editRole !== originalRole;
    const isSelfUpdate = selectedMember.userId === user?.id;
    if (isSelfUpdate && roleChanged) {
      setRoleChangeConfirmDialogOpen(true);
      return;
    }

    await performUpdateRole();
  };

  const handleConfirmRoleChange = async () => {
    setRoleChangeConfirmDialogOpen(false);
    await performUpdateRole();
  };

  const handleDeleteMember = async () => {
    if (!selectedMember) return;

    try {
      setError(null);
      const response = await apiService.removeTeamMember(selectedMember.userId);
      if (response.error) {
        setError(response.error);
      } else {
        setSuccess('Team member removed successfully');
        setDeleteDialogOpen(false);
        setSelectedMember(null);
        loadMembers({ silent: true });
        notifyTeamMembersChanged();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove team member');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'warning';
      case 'MEMBER':
        return 'default';
      case 'PROJECT_MANAGER':
        return 'info';
      case 'ADMIN_MEMBER':
      case 'ADMIN_PROJECT_MANAGER':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'MEMBER':
        return 'Member';
      case 'PROJECT_MANAGER':
        return 'Project Manager';
      case 'ADMIN_MEMBER':
      case 'ADMIN_PROJECT_MANAGER':
        return 'Admin + Project Manager';
      default:
        return role;
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin: Can only access the Manage Account module (Licenses, Subscription, Billing & Invoices, Team Members).';
      case 'MEMBER':
        return 'Member: Can access everything in the app except the Manage Account module.';
      case 'PROJECT_MANAGER':
        return 'Project Manager: Can access all product modules (same as Member) but cannot access the Manage Account module.';
      case 'ADMIN_MEMBER':
      case 'ADMIN_PROJECT_MANAGER':
        return 'Admin + Project Manager: Can access everything (Manage Account module and all product modules).';
      default:
        return '';
    }
  };

  // Auto-hide success message after 10 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const addMemberEmailDomain = getAddMemberEmailDomain();

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {/* Global error snackbar shown at top of Manage Account page */}
      <Snackbar
        open={!!error}
        autoHideDuration={10000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setError(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          role="alert"
          aria-live="assertive"
          data-testid="team-member-feedback-error-snackbar"
          onClose={() => setError(null)}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={10000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          role="alert"
          aria-live="polite"
          data-testid="team-member-feedback-success-snackbar"
          onClose={() => setSuccess(null)}
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Team Members</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenAddMember}
          sx={{ textTransform: 'none' }}
        >
          Add Member
        </Button>
      </Box>

      {/* Padding wrapper: shrinks with AI panel; does not own scroll */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          pb: 1,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Card chrome outside the scroll owner so overflowX:auto is not overridden by Paper */}
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'visible',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            borderRadius: 0,
            margin: 0,
            padding: 0,
          }}
        >
          {/* Single horizontal scroll owner: scrollWidth > clientWidth when AI panel narrows content */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table
              sx={{
                width: '100%',
                minWidth: 1210,
                tableLayout: 'fixed',
              }}
            >
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
              <TableCell sx={{ fontWeight: 600, py: 2, minWidth: 160, width: '18%', whiteSpace: 'nowrap' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2, minWidth: 260, width: '24%', whiteSpace: 'nowrap' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2, minWidth: 200, width: '16%', whiteSpace: 'nowrap' }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2, minWidth: 120, width: '12%', whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2, minWidth: 130, width: '12%', whiteSpace: 'nowrap' }}>Joined</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 2, minWidth: 240, width: '18%', whiteSpace: 'nowrap' }}>Added By</TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  py: 2,
                  minWidth: 100,
                  width: 100,
                  maxWidth: 100,
                  whiteSpace: 'nowrap',
                  px: 1,
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No team members found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {members.map((member, index) => (
                  (() => {
                    const normalizedStatus = String(member.status || '').trim().toUpperCase();
                    const isCurrentUserRow =
                      (user?.id != null && member.userId === user.id) ||
                      (user?.email && member.email && user.email.trim().toLowerCase() === member.email.trim().toLowerCase());
                    const isActiveForDisplay = isCurrentUserRow || normalizedStatus === 'ACTIVE';
                    const isPendingInvite = normalizedStatus === 'PENDING';
                    const statusLabel = isActiveForDisplay
                      ? 'ACTIVE'
                      : isPendingInvite
                        ? 'PENDING'
                        : 'DEACTIVE';
                    const statusColor = isActiveForDisplay
                      ? 'success'
                      : isPendingInvite
                        ? 'warning'
                        : 'error';
                    return (
                  <TableRow 
                    key={member.id}
                    sx={{ 
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                      borderBottom: index < members.length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'
                    }}
                  >
                    <TableCell sx={{ py: 2, minWidth: 160, whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Person sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {member.firstName} {member.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 260, whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {member.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 200, whiteSpace: 'nowrap' }}>
                      <Chip 
                        label={getRoleLabel(member.role)} 
                        color={getRoleColor(member.role)} 
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 120, whiteSpace: 'nowrap' }}>
                      <Chip
                        label={statusLabel}
                        color={statusColor}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 130, whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(member.joinedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, minWidth: 240, whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {member.addedByEmail || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2, verticalAlign: 'middle', minWidth: 100, width: 100, maxWidth: 100, whiteSpace: 'nowrap', px: 1 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0,
                          width: 'auto',
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{ width: 36, height: 36 }}
                          onClick={() => {
                            setSelectedMember(member);
                            const isOrgCreator = !member.addedByEmail || member.addedByEmail === '';
                            // API may return legacy ADMIN_MEMBER; UI uses ADMIN_PROJECT_MANAGER for "Admin + Project Manager".
                            const normalized =
                              member.role === 'ADMIN_MEMBER' ? 'ADMIN_PROJECT_MANAGER' : member.role;
                            const role = isOrgCreator && member.role === 'MEMBER' ? 'ADMIN' : normalized;
                            setEditRole(role);
                            setEditFirstName(member.firstName || '');
                            setEditLastName(member.lastName || '');
                            setEditEmail(member.email || '');
                            setEditDialogOpen(true);
                          }}
                          title="Edit role"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={
                            !currentUserCanRemoveMembers ||
                            member.userId === user?.id ||
                            !member.addedByEmail ||
                            member.addedByEmail === ''
                          }
                          sx={{ width: 36, height: 36 }}
                          onClick={() => {
                            setSelectedMember(member);
                            setDeleteDialogOpen(true);
                          }}
                          title="Remove member"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                    );
                  })()
                ))}
              </>
            )}
          </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>

      {/* Free plan upgrade required */}
      <Dialog
        open={upgradePlanDialogOpen}
        onClose={() => setUpgradePlanDialogOpen(false)}
        maxWidth={false}
        sx={sidebarAwareDialogSx(444)}
      >
        <DialogTitle>Cannot Add Team Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Your Free plan includes 1 user license. That license is already in use. Change an existing
            member&apos;s role to Admin to free the license, or upgrade your plan to add more members.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradePlanDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setUpgradePlanDialogOpen(false);
              closeAddDialog();
              navigate('/manage-account?tab=subscription');
            }}
          >
            Upgrade Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={closeAddDialog}
        maxWidth={false}
        scroll="paper"
        sx={teamInviteDialogSx}
        PaperProps={{
          className: 'team-invite-dialog-paper',
        }}
      >
        <DialogTitle component="div" className="team-invite-dialog-header" sx={{ flexShrink: 0 }}>
          <span className="team-invite-dialog-header-icon" aria-hidden="true">
            <PersonAdd sx={{ color: '#fff', fontSize: 22 }} />
          </span>
          <Typography component="span" variant="h6">
            Invite team member
          </Typography>
        </DialogTitle>
        <DialogContent
          className="team-invite-dialog-body"
          sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send an invitation email with a temporary password. The member signs in and sets their own password.
          </Typography>
          <TextField
            fullWidth
            label="First Name"
            value={newMemberFirstName}
            onChange={(e) => {
              setNewMemberFirstName(e.target.value);
              if (newMemberFirstNameError) {
                setNewMemberFirstNameError(null);
              }
            }}
            margin="dense"
            required
            size="small"
            sx={{ mb: 1.5 }}
            error={!!newMemberFirstNameError}
            helperText={newMemberFirstNameError}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={newMemberLastName}
            onChange={(e) => {
              setNewMemberLastName(e.target.value);
              if (newMemberLastNameError) {
                setNewMemberLastNameError(null);
              }
            }}
            margin="dense"
            required
            size="small"
            sx={{ mb: 1.5 }}
            error={!!newMemberLastNameError}
            helperText={newMemberLastNameError}
          />
          <TextField
            fullWidth
            label="Email"
            type={addMemberEmailDomain ? 'text' : 'email'}
            value={newMemberEmail}
            onChange={(e) => {
              let value = e.target.value.replace(/\s/g, '');
              if (addMemberEmailDomain && value.includes('@')) {
                value = value.substring(0, value.indexOf('@'));
              }
              setNewMemberEmail(value);
              if (newMemberEmailError) {
                setNewMemberEmailError(null);
              }
            }}
            margin="dense"
            required
            size="small"
            sx={{ mb: 1.5 }}
            error={!!newMemberEmailError}
            helperText={newMemberEmailError}
            placeholder={addMemberEmailDomain ? 'username' : undefined}
            InputProps={
              addMemberEmailDomain
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          @{addMemberEmailDomain}
                        </Typography>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
          <FormControl fullWidth margin="dense" size="small" sx={{ mb: 0.5 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={newMemberRole}
              onChange={(e) => setNewMemberRole(e.target.value)}
              label="Role"
            >
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="MEMBER">Member</MenuItem>
              <MenuItem value="PROJECT_MANAGER">Project Manager</MenuItem>
              <MenuItem value="ADMIN_PROJECT_MANAGER">Admin + Project Manager</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
            {getRoleDescription(newMemberRole)}
          </Typography>
          <div className="team-invite-dialog-note">
            Invitations use a dedicated team-member email template with your organization login URL.
          </div>
        </DialogContent>
        <DialogActions className="team-invite-dialog-actions" sx={{ flexShrink: 0 }}>
          <Button onClick={closeAddDialog}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained" disabled={addingMember}>
            {addingMember ? 'Sending invite...' : 'Send invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth={false}
        scroll="paper"
        sx={teamInviteDialogSx}
      >
        <DialogTitle>Update Team Member</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="First Name"
            size="small"
            margin="dense"
            value={editFirstName}
            disabled
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Last Name"
            size="small"
            margin="dense"
            value={editLastName}
            disabled
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            size="small"
            margin="dense"
            value={editEmail}
            disabled
            sx={{ mb: 1.5 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 0.5 }}>
            <InputLabel>Role</InputLabel>
            <Select value={editRole} onChange={(e) => setEditRole(e.target.value)} label="Role">
              <MenuItem value="ADMIN">Admin</MenuItem>
              {(!selectedMember?.addedByEmail || selectedMember.addedByEmail === '') ? null : (
                <MenuItem value="MEMBER">Member</MenuItem>
              )}
              {(!selectedMember?.addedByEmail || selectedMember.addedByEmail === '') ? null : (
                <MenuItem value="PROJECT_MANAGER">Project Manager</MenuItem>
              )}
              <MenuItem value="ADMIN_PROJECT_MANAGER">Admin + Project Manager</MenuItem>
            </Select>
          </FormControl>
          {(!selectedMember?.addedByEmail || selectedMember.addedByEmail === '') ? (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
              Organization creator can only be Admin or Admin + Project Manager. They cannot be changed to Member or Project Manager only.
            </Typography>
          ) : (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
              {getRoleDescription(editRole)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
          {error && editDialogOpen ? (
            <Alert
              severity="error"
              role="alert"
              aria-live="assertive"
              data-testid="team-member-edit-dialog-error"
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          ) : null}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} variant="contained" data-testid="team-member-update-role-button">
              Update
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Self role change confirmation */}
      <Dialog
        open={roleChangeConfirmDialogOpen}
        onClose={() => setRoleChangeConfirmDialogOpen(false)}
        maxWidth={false}
        sx={sidebarAwareDialogSx(444)}
      >
        <DialogTitle>Confirm Role Change</DialogTitle>
        <DialogContent>
          <Typography>
            You are changing your role from{' '}
            {selectedMember ? getRoleLabel(getOriginalEditRole(selectedMember)) : ''} to{' '}
            {getRoleLabel(editRole)}. This will update your role and log you out so you can sign in
            with your new permissions. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleChangeConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => void handleConfirmRoleChange()} variant="contained" color="warning">
            Confirm and Log Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth={false}
        sx={sidebarAwareDialogSx(444)}
      >
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {selectedMember?.firstName} {selectedMember?.lastName} ({selectedMember?.email}) from the team?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteMember} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamMembers;

